'use client'

import React from 'react'
import { useField } from '@payloadcms/ui'
import type { SelectFieldClientComponent } from 'payload'
import ReactSelect, { components } from 'react-select'

import { statusConfigs, statusCategories } from './constants'
import { StatusIcon } from './StatusIcon'
import './styles.scss'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReactSelectProps = any

// Custom Option component with icon
const CustomOption = (props: ReactSelectProps) => {
  const { data } = props
  const config = statusConfigs[data.value] || {
    color: '#6b7280',
    shape: 'circle' as const,
    filled: true,
  }

  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <StatusIcon shape={config.shape} color={config.color} filled={config.filled} size={12} />
        <span>{data.label}</span>
      </div>
    </components.Option>
  )
}

// Custom SingleValue component (selected value)
const CustomSingleValue = (props: ReactSelectProps) => {
  const { data } = props
  const config = statusConfigs[data.value] || {
    color: '#6b7280',
    shape: 'circle' as const,
    filled: true,
  }

  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <StatusIcon shape={config.shape} color={config.color} filled={config.filled} size={12} />
        <span>{data.label}</span>
      </div>
    </components.SingleValue>
  )
}

const StatusField: SelectFieldClientComponent = (props) => {
  const { path, field } = props
  const { value, setValue } = useField<string>({ path })

  // Створюємо мапу всіх опцій
  const allOptionsMap = new Map(
    field.options.map((option) => {
      const optionValue = typeof option === 'string' ? option : option.value
      const optionLabel = typeof option === 'string' ? option : option.label
      return [optionValue, { label: optionLabel, value: optionValue }]
    }),
  )

  // Групуємо опції по категоріях
  const groupedOptions = statusCategories.map((category) => ({
    label: category.label,
    options: category.options
      .map((statusValue) => allOptionsMap.get(statusValue))
      .filter(Boolean) as Array<{ label: string; value: string }>,
  }))

  // Знаходимо вибрану опцію
  const selectedOption = value ? allOptionsMap.get(value) || null : null

  // Get label as string
  const labelText =
    typeof field.label === 'string'
      ? field.label
      : typeof field.label === 'object' && field.label !== null
        ? String(field.label)
        : field.name

  return (
    <div className="status-field">
      <div className="field-type">
        <label className="field-label">
          {labelText}
          {field.required && <span className="required">*</span>}
        </label>
        <ReactSelect
          value={selectedOption}
          onChange={(selectedOption) => {
            if (selectedOption && !Array.isArray(selectedOption)) {
              setValue(selectedOption.value as string)
            }
          }}
          options={groupedOptions}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue,
          }}
          isClearable={false}
          classNamePrefix="rs"
          className="react-select-container"
        />
      </div>
    </div>
  )
}

export default StatusField
